import {
  Button, Card, Descriptions, Modal, Space, Spin, Table, Typography,
} from 'antd';
import rideDriverApi from 'api/rideDriverApi';
import ToastHelper from 'general/helpers/ToastHelper';
import useRouter from 'hooks/useRouter';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getRideDriverErrorMessage,
  RideDriverAccountStatusTag,
  RideDriverProfileStatusTag,
  RideDriverTripStatusTag,
} from '../../rideDriversConstants';

const { Text, Title } = Typography;

function RideDriverDetailScreen() {
  const router = useRouter();
  const driverProfileId = router.query?.driverProfileId;
  const { user, permissionList } = useSelector((state) => state.auth);

  const [driver, setDriver] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [lockLoading, setLockLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [trips, setTrips] = useState([]);
  const [tripsTotal, setTripsTotal] = useState(0);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [tripsPage, setTripsPage] = useState(0);

  const isAdmin = String(user?.role || '').toUpperCase() === 'ADMIN';
  const canManage = isAdmin || permissionList?.some((permission) => permission?.code === 'RIDE_DRIVER_MANAGE');

  const loadDriver = useCallback(async () => {
    if (!driverProfileId) return;
    setDetailLoading(true);
    try {
      const response = await rideDriverApi.getDriver(driverProfileId);
      setDriver(response?.driver || null);
    } catch (error) {
      setDriver(null);
      ToastHelper.showError(getRideDriverErrorMessage(error));
    } finally {
      setDetailLoading(false);
    }
  }, [driverProfileId]);

  const loadTrips = useCallback(async () => {
    if (!driverProfileId) return;
    setTripsLoading(true);
    try {
      const response = await rideDriverApi.getDriverTrips(driverProfileId, { page: tripsPage, limit: 10 });
      setTrips(response?.trips || []);
      setTripsTotal(response?.total || 0);
    } catch (error) {
      setTrips([]);
      setTripsTotal(0);
      ToastHelper.showError(getRideDriverErrorMessage(error));
    } finally {
      setTripsLoading(false);
    }
  }, [driverProfileId, tripsPage]);

  useEffect(() => { loadDriver(); }, [loadDriver]);
  useEffect(() => { loadTrips(); }, [loadTrips]);

  const isLocked = driver?.user?.status === 'SUSPENDED';

  const toggleLock = () => {
    Modal.confirm({
      title: isLocked ? 'Mở lại tài khoản tài xế?' : 'Khoá tài khoản tài xế?',
      content: isLocked
        ? 'Tài xế sẽ có thể đăng nhập và hoạt động trở lại.'
        : 'Tài xế sẽ không thể đăng nhập hoặc nhận chuyến cho đến khi được mở lại.',
      okText: isLocked ? 'Mở lại' : 'Khoá',
      cancelText: 'Huỷ',
      okButtonProps: { danger: !isLocked },
      onOk: async () => {
        setLockLoading(true);
        try {
          const response = await rideDriverApi.setAccountLock(driverProfileId, !isLocked);
          setDriver(response?.driver || null);
          ToastHelper.showSuccess(isLocked ? 'Đã mở lại tài khoản' : 'Đã khoá tài khoản');
        } catch (error) {
          ToastHelper.showError(getRideDriverErrorMessage(error));
        } finally {
          setLockLoading(false);
        }
      },
    });
  };

  const isProfileSuspended = driver?.status === 'SUSPENDED';

  const reactivateProfile = () => {
    Modal.confirm({
      title: 'Kích hoạt lại hồ sơ tài xế?',
      content: 'Hồ sơ đang bị tạm ngưng (tự động do vượt ngưỡng không thực hiện chuyến). Kích hoạt lại sẽ đưa hồ sơ về trạng thái Đã duyệt và reset số lần không đến về 0, cho phép tài xế nhận chuyến trở lại.',
      okText: 'Kích hoạt lại',
      cancelText: 'Huỷ',
      onOk: async () => {
        setReactivateLoading(true);
        try {
          const response = await rideDriverApi.reactivateProfile(driverProfileId);
          setDriver(response?.driver || null);
          ToastHelper.showSuccess('Đã kích hoạt lại hồ sơ');
        } catch (error) {
          ToastHelper.showError(getRideDriverErrorMessage(error));
        } finally {
          setReactivateLoading(false);
        }
      },
    });
  };

  const tripColumns = useMemo(() => [
    { title: 'Mã chuyến', dataIndex: 'tripCode', width: 140 },
    { title: 'Trạng thái', dataIndex: 'status', width: 150, render: (value) => <RideDriverTripStatusTag status={value} /> },
    { title: 'Điểm đi', dataIndex: 'originAddress', ellipsis: true },
    { title: 'Điểm đến', dataIndex: 'destinationAddress', ellipsis: true },
    {
      title: 'Khởi hành', dataIndex: 'departureAt', width: 170,
      render: (value) => (value ? new Date(value).toLocaleString('vi-VN') : '—'),
    },
    {
      title: 'Giá/ghế', dataIndex: 'pricePerSeat', width: 120,
      render: (value) => (value != null ? `${Number(value).toLocaleString('vi-VN')}đ` : '—'),
    },
  ], []);

  if (detailLoading && !driver) {
    return (
      <div className="container-fluid px-4 my-4 text-center py-5">
        <Spin size="large" />
      </div>
    );
  }

  if (!driver) {
    return (
      <div className="container-fluid px-4 my-4">
        <Card>Không tìm thấy tài xế</Card>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4 my-4">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
            <div>
              <Title level={4} className="mb-1">{driver?.user?.fullname || 'Chưa có tên'}</Title>
              <Space size="small">
                <RideDriverProfileStatusTag status={driver.status} />
                <RideDriverAccountStatusTag status={driver?.user?.status} />
              </Space>
            </div>
            {canManage && (
              <Space>
                {isProfileSuspended && (
                  <Button loading={reactivateLoading} onClick={reactivateProfile}>
                    Kích hoạt lại hồ sơ
                  </Button>
                )}
                <Button danger={!isLocked} loading={lockLoading} onClick={toggleLock}>
                  {isLocked ? 'Mở lại tài khoản' : 'Khoá tài khoản'}
                </Button>
              </Space>
            )}
          </div>
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Mã hồ sơ">#{driver.driverProfileId}</Descriptions.Item>
            <Descriptions.Item label="Mã người dùng">#{driver.userId}</Descriptions.Item>
            <Descriptions.Item label="Email">{driver?.user?.email || '—'}</Descriptions.Item>
            <Descriptions.Item label="Điện thoại">{driver?.user?.phone || '—'}</Descriptions.Item>
            <Descriptions.Item label="Đánh giá trung bình">
              {driver.ratingCount > 0 ? `${Number(driver.ratingAverage).toFixed(1)} ★ (${driver.ratingCount} lượt)` : 'Chưa có đánh giá'}
            </Descriptions.Item>
            <Descriptions.Item label="Chuyến hoàn thành">{driver.completedTripCount}</Descriptions.Item>
            <Descriptions.Item label="Tỉ lệ huỷ">{`${(Number(driver.cancellationRate) * 100).toFixed(1)}%`}</Descriptions.Item>
            <Descriptions.Item label="Số lần không đến">{driver.noShowCount}</Descriptions.Item>
            <Descriptions.Item label="Ngày đăng ký">
              {driver?.user?.createdAt ? new Date(driver.user.createdAt).toLocaleString('vi-VN') : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Cập nhật hồ sơ">
              {driver?.updatedAt ? new Date(driver.updatedAt).toLocaleString('vi-VN') : '—'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="Lịch sử chuyến đi">
          <Table
            rowKey="tripId"
            loading={tripsLoading}
            dataSource={trips}
            columns={tripColumns}
            scroll={{ x: 900 }}
            locale={{ emptyText: 'Chưa có chuyến đi nào' }}
            pagination={{
              current: tripsPage + 1,
              pageSize: 10,
              total: tripsTotal,
              onChange: (page) => setTripsPage(page - 1),
            }}
          />
        </Card>
      </Space>
    </div>
  );
}

export default RideDriverDetailScreen;
